/**
 * Centralised React Query key factory. Keeping keys in one place makes
 * invalidation safe and discoverable (e.g. after a mutation we can do
 * `queryClient.invalidateQueries({ queryKey: qk.jobs.all() })`).
 */
export const qk = {
  me: () => ['me'] as const,

  jobs: {
    all: () => ['jobs'] as const,
    list: (opts: { kind?: string; askOnly?: boolean; limit?: number }) =>
      ['jobs', 'list', opts.kind ?? null, opts.askOnly ?? false, opts.limit ?? 30] as const,
    detail: (jobId: string) => ['jobs', 'detail', jobId] as const,
  },

  watchlists: {
    all: () => ['watchlists'] as const,
    list: () => ['watchlists', 'list'] as const,
    detail: (watchlistId: string) => ['watchlists', 'detail', watchlistId] as const,
    summary: (watchlistId: string) => ['watchlists', 'summary', watchlistId] as const,
    feed: (watchlistId: string) => ['watchlists', 'feed', watchlistId] as const,
  },

  items: {
    detail: (itemId: string) => ['items', 'detail', itemId] as const,
    tracks: (itemId: string) => ['items', 'tracks', itemId] as const,
  },

  questionSets: {
    status: (setId: string) => ['questionSets', 'status', setId] as const,
  },

  scenarios: {
    all: () => ['scenarios'] as const,
    list: (opts?: { includeArchived?: boolean }) =>
      ['scenarios', 'list', !!opts?.includeArchived] as const,
    detail: (scenarioId: string) => ['scenarios', 'detail', scenarioId] as const,
    dossier: (scenarioId: string) => ['scenarios', 'dossier', scenarioId] as const,
  },

  companies: {
    all: () => ['companies'] as const,
    list: () => ['companies', 'list'] as const,
    detail: (companyId: number) => ['companies', 'detail', companyId] as const,
    groups: () => ['companies', 'groups'] as const,
    group: (groupId: string) => ['companies', 'group', groupId] as const,
  },

  reviewPatches: (opts: { status?: string; reviewedRunId?: number; limit?: number }) =>
    ['reviewPatches', opts.status ?? null, opts.reviewedRunId ?? null, opts.limit ?? null] as const,

  runs: {
    detail: (runId: number) => ['runs', 'detail', runId] as const,
  },
} as const
